<template>
  <with-loader id="answer-suggestion-form" :loading="isLoading" class="h-full" trigger-loader-on-mount use-as-overlay>
    <form
      class="flex flex-col justify-between bg-white text-gray-dark border-solid border-l border-0 border-soft h-full"
      ref="answerSuggestionContainer"
      :tabindex="0"
      @submit.prevent
      @keydown.enter.prevent="suggestAnswer"
    >
      <div class="flex flex-col pt-1 pb-5 h-full px-4 overflow-auto">
        <div v-if="messageEmailSubject" class="flex flex-col">
          <span class="mt-4 font-bold text-black">Subject</span>
          <transition name="slide-fade" mode="out-in">
            <span class="break-all" :key="messageId">
              {{ messageEmailSubject }}
            </span>
          </transition>
        </div>

        <span class="mt-4 font-bold text-black">Request</span>
        <transition name="slide-fade" mode="out-in">
          <span class="break-all" :key="messageId" v-html="sanitizeRichText(formData.firstRequest)"/>
        </transition>

        <span class="mt-4 font-bold text-black">Original Answer</span>
        <transition v-if="getAnswerByPlatformId(formData.intentSuggestion.originalIntent)" name="slide-fade"
                    mode="out-in">
          <span class="inline-block">
            <router-link :key="messageId"
                        :to="`/bots/${botId}/answers/${getAnswerByPlatformId(formData.intentSuggestion.originalIntent).id}`"
                        target="_blank"
                        class="hover:underline"
            >
              {{ getAnswerByPlatformId(formData.intentSuggestion.originalIntent).name }}
            </router-link>
          </span>
        </transition>

        <div v-if="!isGptBot">
          <div class="flex justify-between">
            <span class="my-4 font-bold text-black">Change Answer to:</span>
            <link-to
              v-if="answerId"
              :id="answerId"
              :class="{'invisible': isNotShown}"
              :is-icon-first="true"
              :to="`/bots/${botId}/answers/${answerId}`"
              label="View Answer"
            >
              <template #icon>
                <answers-icon #icon :is-filled="true" class="h-5"/>
              </template>
            </link-to>
          </div>
          <answer-suggestion-form-control :answers="answerList"
                                          :form-data="formData"
                                          :is-select-open.sync="isSelectOpen"
                                          :suggest-button-id="answerSuggestionButtonId"
                                          :suggested-intent-platform-id="suggestedIntentPlatformId"
                                          :suggested-answer-name="suggestedAnswerName"
                                          :selected-button="selectedButton"
                                          :error-message="errorMessage"
                                          @change-suggested-answer="changeSuggestedAnswer"
                                          @answer-select-blur="handleSelectBlur"
                                          @suggest-answer="suggestAnswer"
                                          @select-button="handleSelectButton"
          />

          <transition name="slide-fade" mode="out-in">
            <intent-suggestion-examples v-if="suggestedIntentPlatformId && !errorMessage"
                                        :intent-id="intentId"
                                        :key="intentId + isSaved"
                                        :suggested-answer-name="suggestedAnswerName"
                                        :answer-id="answerId"
                                        class="h-full mt-4"
                                        v-bar
            />
          </transition>
        </div>
      </div>
    </form>
  </with-loader>
</template>

<script>
import requestsService from '@/services/amio/requests.service'
import answerService from '@/services/amio/answer.service'
import analyticsFilterModule from '@/store/modules/analytics-filter.module'
import conversationModule from '@/store/modules/conversation.module'
import conversationDetailModule from '@/store/modules/conversation-detail.module'
import RadioButton from '@/components/common/RadioButton.vue'
import IntentSuggestionExamples from '@/components/analytics/conversations/detail/AnswerSuggestionExamples.vue'
import LinkTo from '@/components/common/LinkTo.vue'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import toaster from '@/utils/toaster'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import dropdownButtonsUtils from '@/utils/dropdown-button.utils'
import AnswerSuggestionFormControl from '@/components/analytics/conversations/detail/AnswerSuggestionFormControl.vue'
import ConversationNavigationUtils, { conversationNavigationVueUtils } from '@/utils/conversation-navigation.utils'
import answerModule from '@/store/modules/answer.module'
import botModule from '@/store/modules/bot.module'
import AnswersHeader from '@/components/answers/AnswersHeader.vue'
import AnswersIcon from '@/assets/images/icons/AnswersIcon.vue'
import WithLoader from '@/components/common/WithLoader.vue'

export default {
  name: 'AnswerSuggestionForm',

  components: {
    AnswersHeader,
    AnswerSuggestionFormControl,
    RadioButton,
    IntentSuggestionExamples,
    LinkTo,
    AnswersIcon,
    WithLoader
  },

  props: {
    ...conversationNavigationVueUtils().props,

    formData: {
      type: Object,
      required: true
    },
    answersByIntentId: {
      type: Object,
      default: () => {}
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isSaved: false,
      cachedMessageId: null,
      answerSuggestionButtonId: BUSY_BUTTONS.ANSWER_SUGGESTION,
      isSelectOpen: false,
      selectedButton: {},
      stepForwardWasClicked: false,
      stepBackWasClicked: false,
      errorMessage: null
    }
  },

  computed: {
    ...conversationNavigationVueUtils(ConversationNavigationUtils).computed,
    answerList: answerModule.getAnswerList,
    bot: analyticsFilterModule.getBot,
    messages: conversationDetailModule.getMessages,

    botId() {
      return this.bot?.id
    },

    isGptBot() {
      return botModule.isGptBot()
    },

    answer() {
      if (this.isNotShown || isEmpty(this.answersByIntentId)) return null

      const answer = this.answersByIntentId[this.suggestedIntentPlatformId]
      return answer ? {
        name: answer.name,
        id: answer.id,
        intent: { platformId: this.suggestedIntentPlatformId, id: answer.intentId }
      } : {
        name: this.formData.intentSuggestion?.name,
        intent: { platformId: this.suggestedIntentPlatformId }
      }
    },

    answerId() {
      return this.answer?.id
    },

    intentId() {
      return this.answer?.intent?.id
    },

    isNotShown() {
      return !this.suggestedIntentPlatformId
    },

    messageId() {
      return this.formData.messageId
    },

    messageEmailSubject() {
      const message = this.messages.find(message => message.data.message_id === this.messageId)
      return message?.data?.metadata?.subject
    },

    suggestedIntentPlatformId() {
      return this.formData.intentSuggestion?.suggestedIntentPlatformId
    },

    suggestedAnswerName() {
      return this.formData.intentSuggestion?.name || this.answer?.name
    },

    buttonOptions() {
      return dropdownButtonsUtils.getSuggestAnswerOptions()
    }
  },

  watch: {
    isConversationLoading: {
      immediate: false,
      async handler(value) {
        if (value || !this.stepForwardWasClicked) return

        await this.gotToTargetMessage(0)
        this.stepForwardWasClicked = false
      }
    },

    'formData.requestId'() {
      this.errorMessage = null
    }
  },

  methods: {
    ...conversationNavigationVueUtils(ConversationNavigationUtils).methods,

    handleSelectButton(buttonIndex) {
      this.selectedButton = this.buttonOptions[buttonIndex]
      this.$refs.answerSuggestionContainer.focus()
    },

    changeSuggestedAnswer(suggestedAnswer) {
      this.isSaved = false
      this.$emit('change-suggested-answer', suggestedAnswer)
    },

    handleSelectBlur() {
      this.isSelectOpen = false
      this.$refs.answerSuggestionContainer.focus()
    },

    async suggestAnswer() {
      if (this.isSelectOpen) return

      if (!this.suggestedIntentPlatformId) {
        // TODO: when the delete api is created, replace toast with api call
        toaster.error('Please select a "Suggested Answer"')
        return
      }

      try {
        busyButtonsModule.setButtonBusy(this.answerSuggestionButtonId, true)
        if (!this.answerId) {
          const answer = await answerService.create(this.botId, this.suggestedAnswerName, conversationModule.createEmptyJson())
          answer.intent.example = this.formData.firstRequest
          answerModule.setAnswerList(this.answerList.concat(answer))
          await requestsService.saveIntentSuggestion(this.formData.requestId, answer.intent.platformId)
          this.$emit('change-suggested-answer', {
            suggestedIntentPlatformId: answer.intent.platformId,
            suggestedAnswerName: answer.name
          })
        } else {
          await requestsService.saveIntentSuggestion(this.formData.requestId, this.suggestedIntentPlatformId)
        }
        this.$emit('answer-suggested')
        this.isSaved = true
        this.errorMessage = null
        if (this.selectedButton.props.goToNextMessage) await this.gotToTargetMessage(1)

      } catch (err) {
        this.resolveError(err)
      } finally {
        busyButtonsModule.setButtonBusy(this.answerSuggestionButtonId, false)
      }
    },

    getAnswerByPlatformId(platformId) {
      const formattedPlatformId = platformId === '__unknown' ? 'unknown' : platformId
      return this.answersByIntentId[formattedPlatformId]
    },

    resolveError(e) {
      const errorMessage = head(e.response.data.details.errors)?.message
      if (errorMessage.startsWith('example:')) {
        const answerId = errorMessage.split('answerId: ')[1].replace(')', '')
        const answerName = errorMessage.split('answer: ')[1]
        const link = `<a href="answers/${ answerId }" target="_blank">${answerName}</a>`
        this.errorMessage = `Cannot suggest this example. It already exists in answer: ${link}.`
      } else if (errorMessage.startsWith('intentId:')) {
        const answer = this.getAnswerByPlatformId(this.suggestedIntentPlatformId)
        const link = `<a href="answers/${ answer.id }" target="_blank">${answer.name}</a>`
        this.errorMessage = `Cannot suggest this example. It already exists in answer: ${link}.`
      } else {
        this.errorMessage = errorMessage
      }
    }
  },

  async mounted() {
    this.cachedMessageId = this.messageId
  },

  updated() {
    if (this.messageId === this.cachedMessageId) return

    this.cachedMessageId = this.messageId
    this.isSaved = false
  }
}
</script>
